import React, { useEffect, useState } from "react";
import { AlertTriangle, Wrench, Cpu, FileText, Home, Power, DollarSign, Car, Gauge, Building2, Scale, Wallet, LayoutDashboard, Activity, Receipt, Coins, Package, Undo2, UserCog, Globe2, TrendingUp } from "lucide-react";
import { useLocation } from "react-router-dom";
import { isMaintenanceViewer, isSuperAdmin } from "./MaintenanceGuard";
import "./maintenance-layout.scss";

interface UserInfo {
  user_id: string;
  email: string;
  name: string;
  roles: string[];
}

interface MaintenanceSidebarProps {
  className?: string;
}

const MaintenanceSidebar: React.FC<MaintenanceSidebarProps> = ({ className = "" }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const location = useLocation();

  useEffect(() => {
    const storedUser = localStorage.getItem("user_info");
    if (storedUser) {
      try { setUser(JSON.parse(storedUser)); } catch {}
    }
  }, []);

  const handleLogout = () => {
    const idToken = localStorage.getItem("id_token");
    localStorage.clear();
    const zitadelDomain = "https://idp.cms.chargingadda.com";
    const postLogoutRedirectUri = "https://business.chargingadda.com";
    window.location.href =
      `${zitadelDomain}/oidc/v1/end_session?` +
      `id_token_hint=${idToken}&` +
      `post_logout_redirect_uri=${encodeURIComponent(postLogoutRedirectUri)}`;
  };

  const isActive = (path: string) => location.pathname.startsWith(path);
  const firstLetter = user?.name?.charAt(0).toUpperCase() || "U";

  return (
    <aside className={`maint-sidebar ${className}`}>
      <div className="maint-sidebar-logo">
        <span className="maint-sidebar-circle">{firstLetter}</span>
        <h2>{user ? user.name : "Maintenance"}</h2>
      </div>

      <nav className="maint-sidebar-menu">
        <div className="maint-sidebar-section-label">CMS</div>
        <ul>
          <li className={isActive("/dashboard") ? "active" : ""}>
            <a href="/dashboard"><Home size={18} /> Dashboard</a>
          </li>
          <li className={isActive("/account") ? "active" : ""}>
            <a href="/account"><UserCog size={18} /> My Account</a>
          </li>
        </ul>

        <div className="maint-sidebar-section-label">Maintenance</div>
        <ul>
          <li className={isActive("/maintenance/overview") ? "active" : ""}>
            <a href="/maintenance/overview"><LayoutDashboard size={18} /> Overview</a>
          </li>
          <li className={isActive("/maintenance/stations") ? "active" : ""}>
            <a href="/maintenance/stations"><Wrench size={18} /> Stations</a>
          </li>
          <li className={isActive("/maintenance/devices") ? "active" : ""}>
            <a href="/maintenance/devices"><Cpu size={18} /> Devices</a>
          </li>
          <li className={isActive("/maintenance/anomalies") ? "active" : ""}>
            <a href="/maintenance/anomalies"><AlertTriangle size={18} /> Anomalies</a>
          </li>
          <li className={isActive("/maintenance/procurement") ? "active" : ""}>
            <a href="/maintenance/procurement"><Package size={18} /> Procurement</a>
          </li>
          <li className={isActive("/maintenance/pricing") ? "active" : ""}>
            <a href="/maintenance/pricing"><DollarSign size={18} /> Pricing</a>
          </li>
          <li className={isActive("/maintenance/settlement") ? "active" : ""}>
            <a href="/maintenance/settlement"><Scale size={18} /> Settlement</a>
          </li>
          <li className={isActive("/maintenance/org-wallet") ? "active" : ""}>
            <a href="/maintenance/org-wallet"><Wallet size={18} /> Org Wallet</a>
          </li>
          <li className={isActive("/maintenance/refunds") ? "active" : ""}>
            <a href="/maintenance/refunds"><Undo2 size={18} /> Refunds</a>
          </li>
          <li className={isActive("/maintenance/vehicles") ? "active" : ""}>
            <a href="/maintenance/vehicles"><Car size={18} /> Vehicles</a>
          </li>
          <li className={isActive("/maintenance/utilization") ? "active" : ""}>
            <a href="/maintenance/utilization"><Gauge size={18} /> Utilization</a>
          </li>
          <li className={isActive("/maintenance/sessions") ? "active" : ""}>
            <a href="/maintenance/sessions"><Activity size={18} /> Sessions</a>
          </li>
          <li className={isActive("/maintenance/transactions") ? "active" : ""}>
            <a href="/maintenance/transactions"><Receipt size={18} /> Transactions</a>
          </li>
          <li className={isActive("/maintenance/cashback") ? "active" : ""}>
            <a href="/maintenance/cashback"><Coins size={18} /> Cashback</a>
          </li>
          {(isSuperAdmin() || isMaintenanceViewer()) && (
            <li className={isActive("/maintenance/ocpi") ? "active" : ""}>
              <a href="/maintenance/ocpi"><Globe2 size={18} /> OCPI Roaming</a>
            </li>
          )}
          {(isSuperAdmin() || isMaintenanceViewer()) && (
            <li className={isActive("/maintenance/predictions") ? "active" : ""}>
              <a href="/maintenance/predictions"><TrendingUp size={18} /> Predictions</a>
            </li>
          )}
          {isSuperAdmin() && (
            <li className={isActive("/maintenance/zitadel") ? "active" : ""}>
              <a href="/maintenance/zitadel"><Building2 size={18} /> Provisioning</a>
            </li>
          )}
          {isSuperAdmin() && (
            <li className={isActive("/maintenance/audit") ? "active" : ""}>
              <a href="/maintenance/audit"><FileText size={18} /> Audit Log</a>
            </li>
          )}
        </ul>
      </nav>

      <div className="maint-sidebar-footer">
        <button className="maint-sidebar-logout" onClick={handleLogout}>
          <Power size={18} /> Logout
        </button>
      </div>
    </aside>
  );
};

export default MaintenanceSidebar;
